const collectCoordinatePairs = (value, output = []) => {
  if (!Array.isArray(value)) {
    return output
  }

  if (
    value.length >= 2 &&
    Number.isFinite(Number(value[0])) &&
    Number.isFinite(Number(value[1]))
  ) {
    output.push([Number(value[0]), Number(value[1])])
    return output
  }

  value.forEach((item) => collectCoordinatePairs(item, output))
  return output
}

const getFeaturePolygons = (geometry) => {
  if (geometry?.type === 'Polygon') {
    return [geometry.coordinates]
  }

  if (geometry?.type === 'MultiPolygon') {
    return geometry.coordinates
  }

  return []
}

export const getSoilMapBbox = (featureCollection) => {
  const coordinates = collectCoordinatePairs(
    featureCollection?.features?.map((feature) => feature.geometry?.coordinates)
  )

  if (coordinates.length < 3) {
    return null
  }

  const lngs = coordinates.map(([lng]) => lng)
  const lats = coordinates.map(([, lat]) => lat)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)

  if (minLng === maxLng || minLat === maxLat) {
    return null
  }

  return [
    [minLng, maxLat],
    [maxLng, maxLat],
    [maxLng, minLat],
    [minLng, minLat],
  ]
}

const getCanvasPoint = ([lng, lat], bbox, width, height) => {
  const minLng = bbox[0][0]
  const maxLng = bbox[1][0]
  const maxLat = bbox[0][1]
  const minLat = bbox[2][1]
  const lngSpan = maxLng - minLng
  const latSpan = maxLat - minLat

  if (!lngSpan || !latSpan) {
    return null
  }

  return {
    x: ((lng - minLng) / lngSpan) * width,
    y: ((maxLat - lat) / latSpan) * height,
  }
}

export const createSoilMapCanvas = (
  featureCollection,
  bbox = getSoilMapBbox(featureCollection)
) => {
  if (!featureCollection?.features?.length || !bbox || typeof document === 'undefined') {
    return null
  }

  const lngSpan = Math.abs(bbox[1][0] - bbox[0][0]) || 1
  const latSpan = Math.abs(bbox[0][1] - bbox[2][1]) || 1
  const width = 1024
  const height = Math.min(Math.max(Math.round(width * (latSpan / lngSpan)), 256), 1600)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  context.clearRect(0, 0, width, height)
  context.lineJoin = 'round'
  context.lineWidth = Math.max(1, width / 900)
  context.strokeStyle = 'rgba(71, 85, 105, 0.68)'

  featureCollection.features.forEach((feature) => {
    const polygons = getFeaturePolygons(feature.geometry)

    if (!polygons.length) {
      return
    }

    context.beginPath()
    polygons.forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach((coordinate, index) => {
          const point = getCanvasPoint(coordinate, bbox, width, height)

          if (!point) {
            return
          }

          if (index === 0) {
            context.moveTo(point.x, point.y)
          } else {
            context.lineTo(point.x, point.y)
          }
        })
        context.closePath()
      })
    })
    context.fillStyle = feature.properties?.color || '#cbd5a0'
    context.fill('evenodd')
    context.stroke()
  })

  return canvas
}

const getProductivityValue = (item) => {
  const rawValue = String(item?.productivityIndex ?? '').trim()
  if (!rawValue) {
    return null
  }

  const value = Number(rawValue.replace(',', '.'))
  return Number.isFinite(value) ? value : null
}

export const orderSoilMapLegendItems = (items = []) =>
  [...items].sort((first, second) => {
    const firstProductivity = getProductivityValue(first)
    const secondProductivity = getProductivityValue(second)

    if (firstProductivity == null && secondProductivity != null) {
      return 1
    }
    if (firstProductivity != null && secondProductivity == null) {
      return -1
    }
    if (
      firstProductivity != null &&
      secondProductivity != null &&
      firstProductivity !== secondProductivity
    ) {
      return secondProductivity - firstProductivity
    }

    return String(first?.label || '').localeCompare(String(second?.label || ''), undefined, {
      sensitivity: 'base',
    })
  })

const uniqueOfficialLinks = (links = []) => {
  const seen = new Set()

  return links.filter((link) => {
    const href = String(link?.href || '').trim()

    if (!href || seen.has(href)) {
      return false
    }

    seen.add(href)
    return true
  })
}

export const selectSoilMapOfficialLinks = ({
  contextualLinks = [],
  fallbackLink = null,
  viewerLink = null,
}) => {
  const contextual = uniqueOfficialLinks(contextualLinks)

  return uniqueOfficialLinks([
    ...contextual,
    ...(contextual.length ? [] : [fallbackLink]),
    viewerLink,
  ])
}

export const getSoilMapLegendModel = (soilMapData) => ({
  type: 'soil_map',
  items: orderSoilMapLegendItems(soilMapData?.legend || []),
  source: soilMapData?.source || null,
  dataset: soilMapData?.dataset || soilMapData?.source?.dataset || null,
  scales: soilMapData?.scales || [],
  coverage: soilMapData?.coverage || null,
  detailLevel: soilMapData?.detailLevel || null,
  documentation: soilMapData?.documentation || null,
})
